import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import { toast } from "sonner";
import { MessageCircle, Loader2, Send, Trash2 } from "lucide-react";

const formatAgo = (iso) => {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return "";
  }
};

export const CommentsSection = ({ analysisId, ownerUserId }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/community/${analysisId}/comments`);
      setItems(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const submit = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    if (!user) {
      toast(t("comments_login_required"));
      navigate("/login");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post(`/community/${analysisId}/comments`, { body: text });
      setItems((prev) => [data, ...prev]);
      setBody("");
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/community/${analysisId}/comments/${id}`);
      setItems((prev) => prev.filter((c) => c.comment_id !== id));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const canDelete = (c) =>
    user && (user.user_id === c.user_id || user.user_id === ownerUserId);

  return (
    <section className="surface-card p-6 space-y-4" data-testid="comments-section">
      <div className="flex items-center justify-between">
        <span className="label-mono inline-flex items-center gap-1.5">
          <MessageCircle className="h-3 w-3" strokeWidth={2} />
          {t("comments_label")}
        </span>
        <span className="font-mono text-[11px] text-neutral-500">
          {items.length} {t("comments_count_label")}
        </span>
      </div>

      <form onSubmit={submit} className="space-y-2" data-testid="comments-form">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={user ? t("comments_placeholder") : t("comments_login_required")}
          className="bg-[#0e0e0e] border-white/10 rounded-none min-h-[70px] font-mono text-sm focus-visible:ring-bull/40 focus-visible:border-bull/40 resize-none"
          maxLength={600}
          data-testid="comments-input"
        />
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] text-neutral-500">{body.length} / 600</span>
          <Button
            type="submit"
            disabled={posting || !body.trim()}
            size="sm"
            className="rounded-none bg-bull text-black hover:bg-bull/90 h-9 px-4 font-medium disabled:opacity-30"
            data-testid="comments-submit"
          >
            {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
              <><Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} /> {t("comments_post")}</>
            )}
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-bull" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500 font-mono py-3" data-testid="comments-empty">
          {t("comments_empty")}
        </p>
      ) : (
        <ul className="divide-y divide-white/5" data-testid="comments-list">
          {items.map((c) => (
            <li
              key={c.comment_id}
              className="py-3 flex items-start gap-3"
              data-testid={`comment-${c.comment_id}`}
            >
              <span className="h-7 w-7 shrink-0 inline-flex items-center justify-center border border-white/10 bg-white/[0.04] text-bull font-mono text-xs mt-0.5">
                {c.author_initial}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-neutral-200 truncate">{c.author_name}</span>
                  <span className="font-mono text-[10px] text-neutral-500">{formatAgo(c.created_at)}</span>
                </div>
                <p className="mt-1 text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap break-words">
                  {c.body}
                </p>
              </div>
              {canDelete(c) && (
                <button
                  onClick={() => remove(c.comment_id)}
                  className="text-neutral-600 hover:text-bear transition-colors"
                  data-testid={`comment-delete-${c.comment_id}`}
                  aria-label="delete"
                  title={t("comments_delete")}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
